import { CardContent, Stack } from '@mui/material';
import { useState } from 'react';

import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { SnapshotToolbar } from '@/components/snapshots/snapshotToolbar';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import { EonTagRemark } from './eonTagRemark';
import { SingleAzureDiskConfig } from './singleAzureDiskConfig';

import { StepContainer } from '../../wizard/StepContainer';
import type { RestoreAzureVMState } from '../restoreAzureVMWizard';

export const AzureVmDiskConfiguration = (
  props: StepProps<RestoreAzureVMState>
) => {
  const dal = useDAL();
  const [isRestoring, setIsRestoring] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const { RestoreVmWithNoNic } = useFeatureFlags();

  const startRestore = () => {
    setIsRestoring(true);
    // TODO: Add validation against azure that configuration is correct and valid
    setError(undefined);
    const tags = {
      ...(props.wizardState.keepOriginalTags
        ? props.wizardState.initialTags
        : props.wizardState.tags),
      'eon:restore': 'true',
    };
    const diskSettings = props.wizardState.disksState?.map((disk) => ({
      providerDiskId: disk.diskSettings.providerDiskId,
      settings: {
        name: disk.diskSettings.settings.name,
        type: disk.diskSettings.settings.type,
        tier: disk.diskSettings.settings.tier,
        sizeBytes: disk.diskSettings.settings.sizeBytes,
        hyperVGeneration: disk.diskSettings.settings.hyperVGeneration,
        tags: {
          ...(props.wizardState.keepOriginalTags
            ? disk.initialTags
            : disk.tags),
          'eon:restore': 'true',
        },
      },
    }));

    return dal.restore.azureVm
      .restore(props.wizardState.resourceId, props.wizardState.snapshot.id, {
        restoreAccountId: props.wizardState.restoreAccount!.id,
        destination: {
          azureVm: {
            region: props.wizardState.location!,
            vmName: props.wizardState.vmName ?? '',
            resourceGroupName: props.wizardState.resourceGroup!,
            networkInterface: props.wizardState.networkInterface ?? '',
            vmSize: props.wizardState.keepOriginalVMSize
              ? (props.wizardState.originalVMSize ?? '')
              : (props.wizardState.vmSize ?? ''),
            tags,
            // TODO: Implement UI for these fields using azureVMTypes and azureDiskTypes
            disks: diskSettings,
            startInstanceAfterRestore:
              props.wizardState.startInstanceAfterRestore,
          },
        },
      })
      .then(() =>
        props.finish(`Started restore job for ${props.wizardState.vmName}`)
      )
      .catch(async (e) => {
        const errorMessage = await extractErrorMessage(e);
        setIsRestoring(false);
        setError(`Failed to restore disk: ${errorMessage}`);
      });
  };

  return (
    <StepContainer
      sx={{ padding: '0' }}
      navigationComponent={
        error ? (
          <Stack alignItems='center' direction='row' sx={{ color: 'red' }}>
            <i className='ri-error-warning-line mr-[8px] text-[18px]' />
            {error}
          </Stack>
        ) : (
          <EonTagRemark />
        )
      }
      stepperLabels={props.stepperLabels}
      stepperIndex={2}
      nextButtonText='Restore'
      nextButtonLoadingText='Restoring...'
      onBackClick={props.back}
      canGoNext={() =>
        !isRestoring &&
        props.wizardState.vmName !== undefined &&
        props.wizardState.resourceGroup !== undefined &&
        props.wizardState.location !== undefined &&
        (RestoreVmWithNoNic ||
          props.wizardState.networkInterface !== undefined) &&
        props.wizardState.vmSize !== undefined
      }
      onNextClick={startRestore}
      toolbarComponent={
        <SnapshotToolbar
          pointInTime={props.wizardState.snapshot.pointInTime}
          vaultId={props.wizardState.snapshot.vaultId}
          accessDeniedItemsCount={
            props.wizardState.snapshot.accessDeniedItemsCount
          }
          supportPiTR={props.wizardState.snapshot.supportPiTR}
        />
      }
    >
      <CardContent className='p-0'>
        {props.wizardState.disksState?.map((disk) => {
          const initialDiskSettings = props.wizardState.initialDiskStates.find(
            (d) =>
              disk.diskSettings.providerDiskId === d.diskSettings.providerDiskId
          );
          if (
            !initialDiskSettings ||
            !props.wizardState.restoreAccount ||
            !props.wizardState.resourceGroup ||
            !props.wizardState.location
          ) {
            return null;
          }
          return (
            <SingleAzureDiskConfig
              key={disk.diskSettings.providerDiskId}
              diskId={disk.diskSettings.providerDiskId}
              accountID={props.wizardState.restoreAccount.id}
              location={props.wizardState.location}
              initialSettings={initialDiskSettings}
              diskSettings={disk}
              setWizardState={props.setWizardState}
            />
          );
        })}
      </CardContent>
    </StepContainer>
  );
};
